import { Button, Flex, Tabs } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';

import { Schemas } from '@api-client/generated/types';
import { IconPlus } from '@assets';
import { Scrollbar } from '@components';
import { ModalCreditNote } from '@entities/billing/modals/ModalCreditNote/ModalCreditNote';
import { sumTotal } from '@entities/billing/modals/ModalCreditNote/buildCreditNoteItems';
import BillingOneTimeInvoicesItemsList from '@entities/billing/services-list/BillingOneTimeInvoicesItemsList';
import { buildCreditNoteTabs } from '@entities/billing/tabs/buildCreditNoteTabs';
import BillingTabDocument from '@entities/billing/tabs/BillingTabDocument';

import BillingCardSkeleton from '../BillingCardSkeleton';
import * as S from './styled';

enum TabKey {
  Details = 'details',
  PaymentDocument = 'paymentDocument',
}

type BillingOneTimeInvoiceCardProps = {
  details?: Schemas.BillingDocument;
  isLoading: boolean;
  onAfterUpdate: VoidFunction;
};

const BillingOneTimeInvoiceCard = (props: BillingOneTimeInvoiceCardProps) => {
  const [creditNoteParentId, setCreditNoteParentId] = useState<string | null>();

  if (!props.details) {
    return (
      <S.Container>
        <BillingCardSkeleton />
      </S.Container>
    );
  }

  return (
    <>
      <S.Container vertical>
        <S.Header align="flex-start" justify="space-between" gap={16}>
          <Flex vertical>
            <S.Title align="center" gap={8}>
              {dayjs(props.details.documentMetadata.issueDate).format(
                'DD.MM.YYYY'
              )}
            </S.Title>

            <S.Details>
              {props.details.documentMetadata.items?.length} items
            </S.Details>
          </Flex>
        </S.Header>
        <Tabs
          size="large"
          tabBarExtraContent={{
            right: (
              <Button
                icon={<IconPlus />}
                type="link"
                onClick={() => setCreditNoteParentId(props.details?.id || null)}
              >
                Add credit note
              </Button>
            ),
          }}
          items={[
            {
              key: TabKey.Details,
              label: 'Details',
              children: (
                <Scrollbar height="calc(100vh - 365px)" withoutPadding>
                  <BillingOneTimeInvoicesItemsList
                    dataSource={props.details.documentMetadata.items}
                  />
                </Scrollbar>
              ),
            },
            {
              key: TabKey.PaymentDocument,
              label: 'Payment Document',
              children: (
                <Scrollbar height="calc(100vh - 365px)" withoutPadding>
                  <BillingTabDocument key={props.details.id} documentId={props.details.id} />
                </Scrollbar>
              ),
            },
            ...buildCreditNoteTabs(props.details.children),
          ]}
        />
      </S.Container>

      <ModalCreditNote
        isOpen={Boolean(creditNoteParentId)}
        parentId={creditNoteParentId || undefined}
        parentAmount={props.details.documentMetadata.amount ?? undefined}
        parentCurrencyCode={
          props.details.documentMetadata.currencyCode ?? undefined
        }
        parentItems={props.details.documentMetadata.items}
        creditNotes={props.details.children}
        alreadyCreditedTotal={(props.details.children ?? []).reduce(
          (acc, child) => {
            const itemTotal = sumTotal(child.documentMetadata?.items ?? []);
            return acc + (itemTotal || child.documentMetadata?.amount || 0);
          },
          0
        )}
        onAfterCreate={props.onAfterUpdate}
        onClose={() => {
          setCreditNoteParentId(null);
        }}
      />
    </>
  );
};

export default BillingOneTimeInvoiceCard;
